import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import iconData from "../../../../shared/icon/icons.json";
import { KIcon } from "../../../../shared/icon/k-icon";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaFileInput from "../../../ui-kit/kuika-file-input/kuika-file-input";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "./project-definition-icon-popover.scss";
import ProjectDefinitionColorPicker from "./project-definition-color-picker";

interface IIconDefinition {
  name: string;
  tags: string[];
}

type IconPopoverMode = "Icon" | "Image";

interface IIconSnapshot {
  iconName: string;
  iconColor: string;
  iconBackgroundColor: string;
  selectedImage: string;
}

export interface IProjectDefinitionIconPopoverProps {
  open: boolean;
  onOpenChange: (visible: boolean) => void;
  iconName: string;
  iconColor: string;
  iconBackgroundColor: string;
  selectedImage: string;
  setSelectedIcon: (iconName: string) => void;
  setSelectedIconColor: (iconColor: string) => void;
  setSelectedIconBackgroundColor: (iconBackgroundColor: string) => void;
  setSelectedImage: (image: string) => void;
  children: React.ReactNode;
}

const icons: IIconDefinition[] = iconData.icons as IIconDefinition[];

// Ports the existing icon-tab match/relevance sort so search behaviour is unchanged.
const filterAndSortIcons = (searchText: string): IIconDefinition[] => {
  const query: string = searchText.toLowerCase();
  return icons
    .filter(
      (icon: IIconDefinition) =>
        icon.name.toLowerCase().includes(query) || icon.tags.some((tag: string) => tag.toLowerCase().includes(query))
    )
    .sort((a: IIconDefinition, b: IIconDefinition) => {
      const nameA: string = a.name.toLowerCase();
      const nameB: string = b.name.toLowerCase();
      if (nameA.includes(query) && !nameB.includes(query)) return -1;
      if (!nameA.includes(query) && nameB.includes(query)) return 1;
      if (nameA.startsWith(query) && !nameB.startsWith(query)) return -1;
      if (nameA === query && nameB !== query) return -1;
      return nameA.localeCompare(nameB);
    });
};

const ProjectDefinitionIconPopover: React.FC<IProjectDefinitionIconPopoverProps> = ({
  open,
  onOpenChange,
  iconName,
  iconColor,
  iconBackgroundColor,
  selectedImage,
  setSelectedIcon,
  setSelectedIconColor,
  setSelectedIconBackgroundColor,
  setSelectedImage,
  children
}) => {
  const [mode, setMode] = useState<IconPopoverMode>(selectedImage ? "Image" : "Icon");
  const [searchText, setSearchText] = useState<string>("");
  const snapshotRef = useRef<IIconSnapshot | null>(null);

  // Snapshot the committed values when the popover opens so CANCEL can revert.
  useEffect(() => {
    if (open) {
      snapshotRef.current = { iconName, iconColor, iconBackgroundColor, selectedImage };
      setMode(selectedImage ? "Image" : "Icon");
      setSearchText("");
    }
    // Only re-run on open transitions; committed values are captured intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleModeChange = (nextMode: IconPopoverMode): void => {
    if (nextMode === "Icon" && selectedImage) {
      setSelectedImage("");
    }
    setMode(nextMode);
  };

  const readImageFile = (file: File, callback: (result: string) => void): void => {
    const reader: FileReader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result as string));
    reader.readAsDataURL(file);
  };

  const handleImageFiles = (files: File[]): void => {
    const file: File | undefined = files[0];
    if (!file) return;
    const fileSizeMb: number = (file.size ?? 0) / 1024 / 1024;
    if (fileSizeMb > 1) {
      KMainFunctions.displayErrorNotification("You tried to upload a file bigger then 1 MB!");
      return;
    }
    readImageFile(file, (result: string) => setSelectedImage(result));
  };

  const handleCancel = (): void => {
    const snapshot: IIconSnapshot | null = snapshotRef.current;
    if (snapshot) {
      setSelectedIcon(snapshot.iconName);
      setSelectedIconColor(snapshot.iconColor);
      setSelectedIconBackgroundColor(snapshot.iconBackgroundColor);
      setSelectedImage(snapshot.selectedImage);
    }
    onOpenChange(false);
  };

  const renderIconContent = (): React.ReactNode => (
    <>
      <div className="kuika-icon-popover__colors">
        <div className="kuika-icon-popover__field">
          <span className="kuika-icon-popover__field-label">Icon color</span>
          <ProjectDefinitionColorPicker iconColor={iconColor} onChange={setSelectedIconColor} type="iconColor" />
        </div>
        <div className="kuika-icon-popover__field">
          <span className="kuika-icon-popover__field-label">Background color</span>
          <ProjectDefinitionColorPicker
            iconColor={iconBackgroundColor}
            onChange={setSelectedIconBackgroundColor}
            type="backgroundColor"
          />
        </div>
      </div>
      <div className="kuika-icon-popover__field">
        <span className="kuika-icon-popover__field-label">Choose icon</span>
        <div className="kuika-icon-popover__grid-card">
          <KuikaInput
            data-testid="searchIconInput"
            placeholder="Search icon..."
            value={searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
          />
          <div className="kuika-icon-popover__grid" data-testid="iconGrid">
            {filterAndSortIcons(searchText).map((icon: IIconDefinition) => (
              <KuikaTooltip key={icon.name} content={icon.name} delayDuration={0}>
                <KIcon
                  onClick={() => setSelectedIcon(icon.name)}
                  className={classNames("kuika-icon-popover__icon", {
                    "kuika-icon-popover__icon--selected": iconName === icon.name
                  })}
                  iconName={icon.name}
                />
              </KuikaTooltip>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const renderImageContent = (): React.ReactNode => (
    <div className="kuika-icon-popover__field">
      <span className="kuika-icon-popover__field-label">Choose image</span>
      {selectedImage ? (
        <div className="kuika-icon-popover__image-preview">
          <img src={selectedImage} alt="project-icon" />
        </div>
      ) : (
        <KuikaFileInput
          variant="dropzone"
          className="kuika-icon-popover__dropzone"
          accept=".png, .jpg"
          onFiles={handleImageFiles}
        >
          <span className="kuika-icon-popover__dropzone-hint">Drag and Drop here or </span>
          <span data-testid="chooseComputerButton" className="kuika-icon-popover__dropzone-link">
            Choose from computer
          </span>
        </KuikaFileInput>
      )}
      <span className="kuika-icon-popover__image-note">
        The application icon can be a maximum of 1000x1000px and 1MB. Accepted file formats: PNG or JPG
      </span>
    </div>
  );

  const content: React.ReactNode = (
    <div className="kuika-icon-popover">
      <div className="kuika-icon-popover__toggle" role="tablist">
        <button
          type="button"
          data-testid="iconModeToggle"
          className={classNames("kuika-icon-popover__toggle-item", {
            "kuika-icon-popover__toggle-item--active": mode === "Icon"
          })}
          onClick={() => handleModeChange("Icon")}
        >
          Icon
        </button>
        <button
          type="button"
          data-testid="imageModeToggle"
          className={classNames("kuika-icon-popover__toggle-item", {
            "kuika-icon-popover__toggle-item--active": mode === "Image"
          })}
          onClick={() => handleModeChange("Image")}
        >
          Image
        </button>
      </div>
      <div className="kuika-icon-popover__body">{mode === "Icon" ? renderIconContent() : renderImageContent()}</div>
      <div className="kuika-icon-popover__footer">
        <KuikaButton
          data-testid="iconPopoverCancelButton"
          type="text"
          color="neutral"
          size="lg"
          uppercase
          onClick={handleCancel}
        >
          Cancel
        </KuikaButton>
        <KuikaButton
          data-testid="iconPopoverDoneButton"
          type="primary"
          size="lg"
          uppercase
          onClick={() => onOpenChange(false)}
        >
          Done
        </KuikaButton>
      </div>
    </div>
  );

  return (
    <KuikaPopover
      visible={open}
      onVisibleChange={onOpenChange}
      content={content}
      position="bottom"
      align="start"
      className="kuika-icon-popover__surface"
    >
      {children}
    </KuikaPopover>
  );
};

export default ProjectDefinitionIconPopover;
